/**
 * Libellés, couleurs et pictogrammes partagés par les panneaux du HUD :
 * météo, statut des remontées, difficulté des pistes, niveaux de congestion.
 */
import { LEVEL_LABEL, type BuildingKind, type FacilityKind, type Lift, type WeatherRegime } from '@snowchain/shared';
import type { Tone } from './components';
import {
  PgBed,
  PgBuilding,
  PgBus,
  PgChalet,
  PgCloud,
  PgCrate,
  PgCross,
  PgCup,
  PgDrop,
  PgFood,
  PgGroomer,
  PgLamp,
  PgLift,
  PgParking,
  PgPiste,
  PgSkier,
  PgSkis,
  PgSnow,
  PgSnowflake,
  PgStorm,
  PgSun,
  PgThaw,
  PgTicket,
  PgWarehouse,
  type IconType,
} from './icons';

export const REGIME: Record<WeatherRegime, { label: string; icon: IconType }> = {
  grand_beau: { label: 'Grand beau', icon: PgSun },
  nuageux: { label: 'Nuageux', icon: PgCloud },
  neige: { label: 'Neige', icon: PgSnow },
  tempete: { label: 'Tempête', icon: PgStorm },
  redoux: { label: 'Redoux', icon: PgThaw },
};

export const LIFT_STATUS: Record<Lift['status'], { label: string; tone: Tone }> = {
  open: { label: 'Ouverte', tone: 'ok' },
  construction: { label: 'En chantier', tone: 'info' },
  closed: { label: 'Fermée', tone: 'neutral' },
  broken: { label: 'En panne', tone: 'danger' },
  wind: { label: 'Vent fort', tone: 'warn' },
  'off-hours': { label: 'Hors horaires', tone: 'neutral' },
};

export const DIFF_COLOR: Record<string, string> = { verte: 'var(--diff-verte)', bleue: 'var(--diff-bleue)', rouge: 'var(--diff-rouge)', noire: 'var(--diff-noire)' };

/** Pictogramme d'un type de bâtiment ; un type inconnu prend le pictogramme générique. */
const BUILDING_ICON: Partial<Record<string, IconType>> = {
  chalet_accueil: PgChalet,
  restaurant: PgFood,
  shop_rental: PgSkis,
  ticket_office: PgTicket,
  welcome_area: PgBus,
  snow_cannon: PgSnowflake,
  lamp_post: PgLamp,
  hotel: PgBed,
  residence: PgBuilding,
  ski_school: PgSkier,
  first_aid: PgCross,
  snack_bar: PgCup,
  groomer_garage: PgGroomer,
  parking: PgParking,
  reservoir: PgDrop,
};

export function buildingIcon(kind: BuildingKind): IconType {
  return BUILDING_ICON[kind] ?? PgWarehouse;
}

export const FACILITY_ICON: Record<FacilityKind, IconType> = {
  accueil: PgParking,
  ticket: PgTicket,
  rental: PgSkis,
  food: PgFood,
  lift: PgLift,
  piste: PgPiste,
  stock: PgCrate,
};

/** Couleur CSS d'un niveau de congestion (-1 hors service, 0 à 3, null sans mesure). */
export function levelColor(level: number | null): string {
  if (level === null) return 'transparent';
  if (level < 0) return 'var(--lv-off)';
  return `var(--lv-${Math.min(3, level)})`;
}

export function levelLabel(level: number | null): string {
  if (level === null) return 'Pas de mesure';
  if (level < 0) return 'Hors service';
  return LEVEL_LABEL[Math.min(3, level)] as string;
}

export function levelTone(level: number | null): Tone {
  if (level === null || level < 0) return 'neutral';
  return level >= 3 ? 'danger' : level === 2 ? 'warn' : level === 1 ? 'info' : 'ok';
}
